import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query } from '../op-types';

export function createMergedPortfolioQueries(client: StoxlyClient) {
  return createQueryKeys('mergedPortfolios', {
    // ['mergedPortfolios', 'all', { query }]
    all: (query?: Query<'mergedPortfoliosFindAll'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.mergedPortfoliosFindAll({ queries: query, signal }),
    }),

    // ['mergedPortfolios', 'detail', mergedPortfolioId, { query }, ...]
    detail: (mergedPortfolioId: string, query?: Query<'mergedPortfoliosFindOne'>) => ({
      queryKey: [mergedPortfolioId, { query }],
      queryFn: ({ signal }) =>
        client.mergedPortfoliosFindOne({
          params: { id: mergedPortfolioId },
          queries: query,
          signal,
        }),
      contextQueries: {
        analytics: (query?: Query<'mergedPortfoliosGetAnalytics'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.mergedPortfoliosGetAnalytics({
              params: { id: mergedPortfolioId },
              queries: query,
              signal,
            }),
        }),
        transactions: (query?: Query<'mergedPortfoliosGetTransactions'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.mergedPortfoliosGetTransactions({
              params: { id: mergedPortfolioId },
              queries: query,
              signal,
            }),
        }),
        positions: () => ({
          queryKey: ['positions'],
          queryFn: ({ signal }) =>
            client.mergedPortfoliosGetPositions({ params: { id: mergedPortfolioId }, signal }),
        }),
        snapshot: () => ({
          queryKey: ['snapshot'],
          queryFn: ({ signal }) =>
            client.mergedPortfoliosGetSnapshot({ params: { id: mergedPortfolioId }, signal }),
        }),
        summaryMetrics: (query?: Query<'mergedPortfoliosGetSummaryMetrics'>) => ({
          queryKey: ['summary-metrics', { query }],
          queryFn: ({ signal }) =>
            client.mergedPortfoliosGetSummaryMetrics({
              params: { id: mergedPortfolioId },
              queries: query,
              signal,
            }),
        }),
      },
    }),
  });
}

export function createMergedPortfolioMutations(client: StoxlyClient) {
  return {
    create: {
      mutationFn: (body: Body<'mergedPortfoliosCreate'>) => client.mergedPortfoliosCreate(body),
    },
    update: {
      mutationFn: ({ id, body }: { id: string; body: Body<'mergedPortfoliosUpdate'> }) =>
        client.mergedPortfoliosUpdate(body, { params: { id } }),
    },
    delete: {
      mutationFn: (id: string) => client.mergedPortfoliosDelete(undefined, { params: { id } }),
    },
    addMember: {
      mutationFn: ({ id, body }: { id: string; body: Body<'mergedPortfoliosAddMember'> }) =>
        client.mergedPortfoliosAddMember(body, { params: { id } }),
    },
    removeMember: {
      mutationFn: ({ id, portfolioId }: { id: string; portfolioId: string }) =>
        client.mergedPortfoliosRemoveMember(undefined, { params: { id, portfolioId } }),
    },
  };
}
